import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Star, Trophy, Users } from "lucide-react";
import { useReveal } from "@/lib/useReveal";

const trainers = [
  {
    id: "t1",
    name: "Marcus Reeves",
    title: "Head Strength Coach",
    img: "https://images.unsplash.com/photo-1577744168855-0391d2ed2b3a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxwZXJzb25hbCUyMHRyYWluZXIlMjBwb3J0cmFpdCUyMGRhcmslMjBzdHVkaW98ZW58MHx8fGJsYWNrfDE3NzcyOTUzMzB8MA&ixlib=rb-4.1.0&q=85",
    specialties: ["Powerlifting", "Hypertrophy", "Nutrition"],
    years: 12,
    rating: 4.9,
    athletes: 1820,
    bio: "Ex-national powerlifter. Obsessed with programming you can actually stick to.",
    accent: "lime",
  },
  {
    id: "t2",
    name: "Sana Iyer",
    title: "Conditioning & Mobility",
    img: "https://images.unsplash.com/photo-1434847868581-86e8a2b8e7a3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxwZXJzb25hbCUyMHRyYWluZXIlMjBwb3J0cmFpdCUyMGRhcmslMjBzdHVkaW98ZW58MHx8fGJsYWNrfDE3NzcyOTUzMzB8MA&ixlib=rb-4.1.0&q=85",
    specialties: ["HIIT", "Mobility", "Recovery"],
    years: 9,
    rating: 5.0,
    athletes: 1240,
    bio: "Precision conditioning for busy lifters. Heart-rate based, form-first.",
    accent: "cyan",
  },
  {
    id: "t3",
    name: "Diego Alvarez",
    title: "Endurance & Zone Training",
    img: "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
    specialties: ["Running", "Cycling", "Zone 2"],
    years: 8,
    rating: 4.8,
    athletes: 980,
    bio: "Turned 35 weekend warriors into sub-4-hour marathoners last year.",
    accent: "orange",
  },
];

const accentMap = {
  lime: {
    ring: "ring-neon-lime/40",
    pillText: "text-neon-lime",
    pillBg: "bg-neon-lime/10 border-neon-lime/25",
    hover: "hover:border-neon-lime/40 hover:shadow-glow-lime",
    grad: "from-neon-lime/25",
  },
  cyan: {
    ring: "ring-neon-cyan/40",
    pillText: "text-neon-cyan",
    pillBg: "bg-neon-cyan/10 border-neon-cyan/25",
    hover: "hover:border-neon-cyan/40 hover:shadow-glow-cyan",
    grad: "from-neon-cyan/25",
  },
  orange: {
    ring: "ring-neon-orange/40",
    pillText: "text-neon-orange",
    pillBg: "bg-neon-orange/10 border-neon-orange/25",
    hover: "hover:border-neon-orange/40 hover:shadow-glow-orange",
    grad: "from-neon-orange/25",
  },
};

export const Trainers = () => {
  const ref = useReveal();
  return (
    <section id="trainers" data-testid="trainers-section" className="relative py-20 sm:py-24">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className="reveal text-center max-w-2xl mx-auto mb-12">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-3 py-1 text-xs uppercase tracking-widest text-white/70">
            <Users className="h-3.5 w-3.5 text-neon-lime" />
            Coaches
          </div>
          <h2 className="mt-4 font-display text-4xl sm:text-5xl tracking-tight text-white">
            MEET THE HUMANS <span className="text-neon-lime">BEHIND THE PLAN</span>
          </h2>
          <p className="mt-3 text-white/70">
            Certified, competitive, and in your corner. Real coaches — not chatbots.
          </p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {trainers.map((t) => (
            <TrainerCard key={t.id} t={t} />
          ))}
        </div>
      </div>
    </section>
  );
};

const TrainerCard = ({ t }) => {
  const ref = useReveal();
  const a = accentMap[t.accent];
  return (
    <Card
      ref={ref}
      data-testid="trainer-profile-card"
      className={`reveal card-lift bg-white/[0.03] border-white/10 rounded-2xl overflow-hidden ${a.hover}`}
    >
      <div className="relative h-48 overflow-hidden">
        <img src={t.img} alt={`${t.name} portrait`} className="w-full h-full object-cover" loading="lazy" />
        <div className={`absolute inset-0 bg-gradient-to-t ${a.grad} to-transparent opacity-70 mix-blend-screen`} />
        <div className="absolute inset-0 bg-gradient-to-t from-ink-0 via-ink-0/40 to-transparent" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 rounded-full border border-white/10 bg-black/50 px-2.5 py-1 text-[11px] text-white">
          <Star className="h-3.5 w-3.5 fill-neon-lime text-neon-lime" />
          <span className="font-mono-num">{t.rating.toFixed(1)}</span>
        </div>
        <div className="absolute bottom-3 left-3 right-3 flex items-center gap-3">
          <Avatar className={`h-12 w-12 ring-2 ${a.ring}`}>
            <AvatarImage src={t.img} alt={t.name} />
            <AvatarFallback className="bg-ink-2 text-white">
              {t.name.split(" ").map((p) => p[0]).join("")}
            </AvatarFallback>
          </Avatar>
          <div>
            <div className="text-white font-semibold leading-tight">{t.name}</div>
            <div className="text-xs text-white/70">{t.title}</div>
          </div>
        </div>
      </div>
      <CardContent className="p-5">
        <p className="text-sm text-white/70 leading-relaxed">{t.bio}</p>
        <div className="mt-4 flex flex-wrap gap-2">
          {t.specialties.map((s) => (
            <Badge key={s} className={`border rounded-full px-2.5 py-1 text-[11px] font-medium ${a.pillBg} ${a.pillText}`}>
              {s}
            </Badge>
          ))}
        </div>
        <div className="mt-5 grid grid-cols-2 gap-3">
          <Meta icon={Trophy} label="Experience" value={`${t.years} years`} />
          <Meta icon={Users} label="Athletes coached" value={t.athletes.toLocaleString()} />
        </div>
      </CardContent>
    </Card>
  );
};

const Meta = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 flex items-center gap-3">
    <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg bg-white/5 border border-white/10 text-white/80">
      <Icon className="h-4 w-4" />
    </span>
    <div className="min-w-0">
      <div className="text-[10px] uppercase tracking-widest text-white/50 truncate">{label}</div>
      <div className="text-sm text-white font-semibold font-mono-num truncate">{value}</div>
    </div>
  </div>
);
